/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import { useContext, useEffect } from "react"
import { useSelector } from "react-redux"
import { useParams } from "react-router-dom"
import { Button, Flex, Typography } from "@duxton-web/ui"
import { useMenuEditorStatus } from "pages/menu/detail/hooks/useMenuEditorStatus"
import {
  AddCircleFilled,
  EditableFilled,
  DownloadUniversal,
} from "@duxton-web/icons"
import { MenuEditorTabKey, menuEditorTabKeys } from "constants/menu"
import {
  HELP_CENTER_BULK_UPLOAD_MENU_LINKS,
  HELP_CENTER_BULK_UPLOAD_MART_MENU_LINKS,
  getHelpCenterBulkUploadLink,
} from "constants/menu_bulk_upload"
import {
  BulkUploadContext,
  DRAWER_STATES,
} from "pages/menu/detail/bulkUpload/BulkUploadContext"
import { isOngoingTaskFoundSelector } from "store/duck/bulkUpload/utils"
import { useTranslations } from "common/utils/i18n"
import { ACTION_TYPE } from "types/delv-platform-api/inventoryCategory"
import { AppState } from "store/store.type"
import { getGrabXValueFromLocalStorage, MerchantPortalFeatureFlag } from "grabx"
import { storageHelper } from "utils/storage"

import { useScribeBulkUploadActions } from "../scribe/useScribeBulkUploadActions"
import { bulkActionDesc } from "./styles"

const { Title } = Typography

const BulkUploadActionsMenu = () => {
  const { tabKey } = useParams<{ tabKey: MenuEditorTabKey }>()
  const isOngoingTaskFound = useSelector(isOngoingTaskFoundSelector)

  const { isEditAllowed } = useMenuEditorStatus()

  const isMartStore = useSelector((state: AppState) => state.menu.isMartStore)

  const enableBulkUploadEditRestriction = getGrabXValueFromLocalStorage<
    boolean
  >(MerchantPortalFeatureFlag.portalEnableBulkUploadEditRestriction)

  const isEditingDisabled = enableBulkUploadEditRestriction && !isEditAllowed

  const {
    trackBulkUploadPageView,
    trackCreateItemsClick,
    trackEditItemsClick,
    trackDownloadCatalogueClick,
    trackLearnMoreClick,
  } = useScribeBulkUploadActions()

  const [
    bulkUploadTitle,
    bulkUploadMenuText,
    createMenuTitle,
    updateMenuTitle,
    downloadCatalogueText,
    learnBulkUploadText,
  ] = useTranslations([
    "menu.tabs.bulkUpload",
    "bulkUpload.actions.menuHeader",
    "bulkUpload.actions.addItem.title",
    "bulkUpload.actions.updateItem.title",
    "bulkUpload.actions.downloadCatalogueMenu",
    "bulkUpload.actions.learnBulkUploadMenu",
  ])

  const {
    setActionType,
    setDrawerState,
    setDrawerVisible,
    setDrawerStateTransitionDisabled,
  } = useContext(BulkUploadContext)

  const handleClickEditMenu = () => {
    setActionType(ACTION_TYPE.UPDATE)
    setDrawerVisible(true)
    trackEditItemsClick()
  }

  const handleClickCreateMenu = () => {
    setActionType(ACTION_TYPE.CREATE)
    setDrawerVisible(true)
    trackCreateItemsClick()
  }

  const handleClickDownloadCatalogue = () => {
    setActionType(ACTION_TYPE.UPDATE)
    setDrawerState(DRAWER_STATES.DOWNLOAD_CATALOGUE)
    setDrawerVisible(true)
    setDrawerStateTransitionDisabled(true)
    trackDownloadCatalogueClick()
  }

  const handleClickLearnMore = () => {
    trackLearnMoreClick()

    const country = storageHelper.getUserCountry() || "SG"
    const language = storageHelper.getLang() || "en"

    const links = isMartStore
      ? HELP_CENTER_BULK_UPLOAD_MART_MENU_LINKS
      : HELP_CENTER_BULK_UPLOAD_MENU_LINKS

    const helpCenterLink = getHelpCenterBulkUploadLink(links, country, language)
    window.open(helpCenterLink, "_blank")?.focus()
  }

  useEffect(() => {
    if (tabKey === menuEditorTabKeys.bulkUploadMenu) {
      trackBulkUploadPageView()
    }
  }, [tabKey, trackBulkUploadPageView])

  return (
    <div>
      <Title level={3}>{bulkUploadTitle}</Title>
      <p css={bulkActionDesc}>
        {bulkUploadMenuText}{" "}
        <span onClick={handleClickLearnMore}>{learnBulkUploadText}</span>
      </p>
      <Flex gap={10}>
        <Button
          icon={<AddCircleFilled />}
          onClick={handleClickCreateMenu}
          disabled={isOngoingTaskFound || isEditingDisabled}
        >
          {createMenuTitle}
        </Button>
        <Button
          icon={<EditableFilled />}
          onClick={handleClickEditMenu}
          disabled={isOngoingTaskFound || isEditingDisabled}
        >
          {updateMenuTitle}
        </Button>
        <Button
          icon={<DownloadUniversal />}
          onClick={handleClickDownloadCatalogue}
        >
          {downloadCatalogueText}
        </Button>
      </Flex>
    </div>
  )
}

export default BulkUploadActionsMenu
